import React, { useState, useEffect } from 'react';
import SvgClose from './icons/SvgClose';
import SvgCopy from './icons/SvgCopy';

const NetworkGrowthPopup = ({ isVisible, onClose, userId }) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    // Check if popup was previously dismissed
    const dismissed = localStorage.getItem('networkGrowthPopupDismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
    }
  }, []);

  const handleClose = () => {
    setIsDismissed(true);
    localStorage.setItem('networkGrowthPopupDismissed', 'true');
    onClose();
  };

  const handleCopyLink = async () => {
    const referralLink = `${window.location.origin}/referral/${userId}`;
    
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setShowConfetti(true);
      
      // Hide confetti after animation
      setTimeout(() => setShowConfetti(false), 3000);
      
      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  if (!isVisible || isDismissed) {
    return null;
  }

  return (
    <>
      <style>
        {`
          @keyframes confetti-fall {
            0% {
              transform: translateY(-100px) rotate(0deg);
              opacity: 1;
            }
            100% {
              transform: translateY(100px) rotate(720deg);
              opacity: 0;
            }
          }
        `}
      </style>
      <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-lg mx-4">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(30)].map((_, i) => {
            const size = Math.random() * 3 + 1;
            const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];
            return (
              <div
                key={i}
                className="absolute opacity-90"
                style={{
                  width: `${size}px`,
                  height: `${size}px`,
                  left: `${50 + (Math.random() - 0.5) * 20}%`,
                  top: `${50 + (Math.random() - 0.5) * 20}%`,
                  backgroundColor: colors[Math.floor(Math.random() * colors.length)],
                  animation: `confetti-fall ${2 + Math.random() * 2}s ease-out forwards`,
                  animationDelay: `${Math.random() * 0.5}s`,
                  transform: `rotate(${Math.random() * 360}deg)`,
                  borderRadius: Math.random() > 0.5 ? '50%' : '0%',
                }}
              />
            );
          })}
        </div>
      )}
      <div className="bg-white rounded-2xl shadow-2xl border-2 border-blue-200 p-8 relative transform hover:scale-105 transition-all duration-300 ring-4 ring-blue-100">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors bg-gray-100 hover:bg-gray-200 rounded-full p-2"
          aria-label="Close popup"
        >
          <SvgClose className="w-6 h-6" />
        </button>

        {/* Content */}
        <div className="flex items-start space-x-6">
          {/* Network icon */}
          <div className="flex-shrink-0">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-xl">
              <svg
                className="w-12 h-12 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                {/* Network/Community Icon */}
                <circle cx="6" cy="6" r="3" fill="currentColor"/>
                <circle cx="18" cy="6" r="3" fill="currentColor"/>
                <circle cx="6" cy="18" r="3" fill="currentColor"/>
                <circle cx="18" cy="18" r="3" fill="currentColor"/>
                <circle cx="12" cy="12" r="4" fill="currentColor" opacity="0.8"/>
                <path d="M6 9L12 12L18 9" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M9 6L12 12L9 18" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M15 6L12 12L15 18" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M6 15L12 12L18 15" stroke="currentColor" strokeWidth="1.5" fill="none"/>
              </svg>
            </div>
          </div>

          {/* Text content */}
          <div className="flex-1 min-w-0">
            <h3 className="text-3xl font-bold text-gray-900 mb-3 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Grow your Good Network
            </h3>
            <p className="text-lg text-gray-700 mb-6 font-medium">
              Know someone doing good? Invite them to join your network!
            </p>
            
            {/* Copy Link Button */}
            <button
              onClick={handleCopyLink}
              className={`w-full flex items-center justify-center space-x-3 px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 ${
                copied 
                  ? 'bg-gradient-to-r from-green-400 to-green-500 text-white shadow-lg shadow-green-200' 
                  : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg shadow-blue-200'
              }`}
            >
              <SvgCopy className="w-6 h-6" />
              <span>{copied ? '🎉 Link Copied!' : 'Copy Referral Link'}</span>
            </button>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default NetworkGrowthPopup;
